import { Button } from '@sortlist-frontend/design-system';
import { ArrowRight, FileText } from '@sortlist-frontend/design-system/icons';
import { useState } from 'react';

import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import type { GuideCta, GuideField, GuideLabels } from '../types';
import { NumberField } from './NumberField';

interface BriefBuilderProps {
  fields: readonly (GuideField & { dataLabel: string })[];
  outTitle: string;
  copyLabel: string;
  cta: GuideCta;
  placeholder: string;
  labels: Pick<GuideLabels, 'copied' | 'copyFallback'>;
}

export const BriefBuilder = (props: BriefBuilderProps) => {
  const { fields, outTitle, copyLabel, cta, placeholder, labels } = props;
  const [values, setValues] = useState<Record<string, string>>({});
  const { label, copy } = useCopyToClipboard({ idle: copyLabel, copied: labels.copied, fallback: labels.copyFallback });
  const brief = fields.map((field) => `${field.dataLabel} : ${values[field.id]?.trim() || placeholder}`).join('\n');

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
      <form className="space-y-3" onSubmit={(event) => event.preventDefault()}>
        {fields.map((field) => (
          <NumberField
            key={field.id}
            field={field}
            value={values[field.id] ?? ''}
            onChange={(value) => setValues({ ...values, [field.id]: value })}
            isText
          />
        ))}
      </form>
      <div className="rounded-2xl border border-primary-200 bg-primary-50 p-4 md:p-5 lg:sticky lg:top-24">
        <p className="mb-2 flex items-center gap-2 font-bold text-secondary-950">
          <FileText size={18} aria-hidden="true" />
          {outTitle}
        </p>
        <pre className="whitespace-pre-wrap rounded-xl bg-white p-4 font-sans text-sm leading-relaxed text-secondary-700">
          {brief}
        </pre>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button color="primary" variant="bordered" size="md" onPress={() => void copy(brief)}>
            {label}
          </Button>
          <Button as="a" href={cta.href} color="primary" variant="solid" size="md" endContent={<ArrowRight />}>
            {cta.label}
          </Button>
        </div>
      </div>
    </div>
  );
};
